import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { memoize } from 'lodash';
import { JUMP_SPEED_THRESHOLD, STATISTIC_FILTER_SIZE } from './constants';
import type { Point } from './store';
import { fastDistance, median } from './utils';
import { binarySearchIndexLo } from './utils/binarySearch';
import type TrackView from './utils/TrackView';

// Returns meters per second
export function* calculateSpeeds(slice: Array<Point>): Generator<number, void, void> {
  let lastLng = 0;
  let lastLat = 0;
  let lastTime = -1;
  let lastOffset = -1;
  for (const p of slice) {
    if (lastTime !== -1) {
      const distance =
        p.offset >= 0 && lastOffset >= 0
          ? p.offset - lastOffset
          : fastDistance(lastLng, lastLat, p.lng, p.lat);
      const speed = distance / (p.time - lastTime);
      if (distance >= 0 && speed < JUMP_SPEED_THRESHOLD) {
        yield speed * OneSecondInMillis;
      }
    }
    lastLng = p.lng;
    lastLat = p.lat;
    lastTime = p.time;
    lastOffset = p.offset;
  }
}

// Returns meters per second
export function medianSpeed(points: Array<Point>): number {
  return median(Array.from(calculateSpeeds(points)).filter((a) => a > 0));
}

// Returns meters per second
export function meanSpeed(points: Array<Point>): number {
  let sum = 0;
  let count = 0;
  for (const speed of calculateSpeeds(points)) {
    sum += speed;
    count += 1;
  }
  return sum / count;
}

function speedFromOffsets(lo: Point, hi: Point) {
  const deltaOffset = hi.offset - lo.offset;
  const deltaTime = hi.time - lo.time;
  return Math.abs(deltaTime === 0 ? 0.0 : deltaOffset / deltaTime);
}

export function getInterpolatedOffset(points: Array<Point>, t: number, stepWidth: number): number {
  const _lo = binarySearchIndexLo(points, (a, i) =>
    a[i].offset < 0
      ? 1 // 1 when `-1` values are at beginning of the track, -1 when at end (not the case)
      : t - a[i].time,
  );
  const _hi = _lo + 1 < points.length ? _lo + 1 : -1;

  if (_lo >= 0) {
    const lo = points[_lo];
    if (lo.offset !== -1) {
      if (_hi >= 0) {
        const hi = points[_hi];
        if (hi.offset !== -1) {
          if (lo.time + stepWidth === hi.time && hi.offset !== -1) {
            // Between two points of a track chunk
            if (speedFromOffsets(lo, hi) <= JUMP_SPEED_THRESHOLD && hi.offset >= lo.offset) {
              // Not a jump
              const fraction = (t - lo.time) / stepWidth;
              return lo.offset * (1 - fraction) + hi.offset * fraction;
            }
          }
        }
      }
      return lo.offset;
    }
  }
  return -1;
}

function meanElevation(shadowTrack: TrackView, start: number, end: number): number {
  const _start = Math.max(0, start);
  const _end = Math.min(shadowTrack.size() - 1, end);
  let sum = 0;
  const count = _end - _start;
  if (count === 0) return 0;

  for (let i = _start; i < _end; i++) {
    sum += shadowTrack.getElevation(i);
  }
  return sum / count;
}

export const memoizedAscent = memoize((shadowTrack: TrackView) => {
  if (shadowTrack.size() === 0) return [];
  const outputSize = Math.ceil(shadowTrack.size() / STATISTIC_FILTER_SIZE);
  const output = Array(outputSize);
  let lastElevation = meanElevation(shadowTrack, 0, STATISTIC_FILTER_SIZE);
  let accumulatedDescent = 0;
  for (let i = 0; i < outputSize; i++) {
    const currentElevation = meanElevation(
      shadowTrack,
      i * STATISTIC_FILTER_SIZE,
      (i + 1) * STATISTIC_FILTER_SIZE,
    );
    if (currentElevation > lastElevation) accumulatedDescent += currentElevation - lastElevation;
    output[i] = accumulatedDescent;
    lastElevation = currentElevation;
  }
  return output;
});

export const memoizedDescent = memoize((shadowTrack: TrackView) => {
  if (shadowTrack.size() === 0) return [];
  const outputSize = Math.ceil(shadowTrack.size() / STATISTIC_FILTER_SIZE);
  const output = Array(outputSize);
  let lastElevation = meanElevation(shadowTrack, 0, STATISTIC_FILTER_SIZE);
  let accumulatedDescent = 0;
  for (let i = 0; i < outputSize; i++) {
    const currentElevation = meanElevation(
      shadowTrack,
      i * STATISTIC_FILTER_SIZE,
      (i + 1) * STATISTIC_FILTER_SIZE,
    );
    if (currentElevation < lastElevation) accumulatedDescent += lastElevation - currentElevation;
    output[i] = accumulatedDescent;
    lastElevation = currentElevation;
  }
  return output;
});
